import Draggable16 from '@carbon/icons-react/es/draggable/16';
import { useSize } from 'ahooks';
import { Space, Checkbox } from 'antd';
import { DataNode, TreeProps } from 'antd/lib/tree';
import React, { Key, useRef, useState } from 'react';
import styled from 'styled-components';
import { View, ViewCustomisationUpdate } from 'api/views/types';
import { Tree as TreeAtom } from 'components/organisms/projects/files/rearrange/components';
import { doLinearDragAndDrop } from 'utils/tree';
import { CustomisationState } from './types';

interface CustomisationTreeProps {
  views: CustomisationState['views'];
  doUpdate: (update: ViewCustomisationUpdate) => void;
}

const StyledTree = styled(TreeAtom)`
  min-height: 300px;
  margin: 0 var(--spacing-05);
  padding: var(--spacing-03) 0;

  .ant-tree-switcher {
    width: var(--spacing-05);
  }

  .ant-tree-iconEle {
    margin-right: var(--spacing-04) !important;
  }
`;

const ViewName = styled.span`
  font-size: var(--font-size-base);
  line-height: var(--line-height-body-short-01);
  color: var(--gray-80);
`;

const createKey = (view: Pick<View<unknown>, 'viewId' | 'hidden'>) =>
  `${view.viewId}|${view.hidden ?? false}`;

const extractKey = (key: Key) => {
  const [viewId, hidden] = (key as string).split('|');

  return {
    viewId: parseInt(viewId, 10),
    hidden: hidden === 'true',
  };
};

export const CustomisationTree = ({
  views,
  doUpdate,
}: CustomisationTreeProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = useRef<any>(null);
  const size = useSize(document.body);
  const treeHeight = (size.height ?? 0) * 0.6;
  const [treeData, setTreeData] = useState<DataNode[]>(
    views?.map((view) => ({
      title: view.viewName,
      key: createKey(view),
    })) ?? [],
  );

  const updateVisibility = (viewId: number, hidden: boolean) => {
    setTreeData((prev) => {
      const index = prev.findIndex(
        (view) => extractKey(view.key).viewId === viewId,
      );

      prev[index].key = createKey({
        ...extractKey(prev[index].key),
        hidden,
      });

      return prev;
    });

    doUpdate({ viewId, hidden });
  };

  const updatePosition = (viewId: number, position: number) => {
    const sanitizedPosition = Math.max(0, Math.min(position, treeData.length));

    setTreeData((prev) => {
      const oldPosition = prev.findIndex(
        (view) => extractKey(view.key).viewId === viewId,
      );

      const updated = prev.filter((_, i) => i !== oldPosition);

      updated.splice(sanitizedPosition, 0, prev[oldPosition]);

      return updated;
    });

    doUpdate({ viewId, position: sanitizedPosition });
  };

  const onDrop: TreeProps['onDrop'] = (info) => {
    const { dragKey, newPosition } = doLinearDragAndDrop(treeData, info);
    const { viewId } = extractKey(dragKey);

    updatePosition(viewId, newPosition);
  };

  if (treeData.length === 0) {
    return null;
  }

  return (
    <StyledTree
      ref={ref}
      draggable
      blockNode
      showIcon
      selectable={false}
      height={treeHeight}
      onDrop={onDrop}
      treeData={treeData}
      onDragOver={({ node: { key } }) => {
        ref.current?.scrollTo({ key, offset: 10 });
      }}
      titleRender={({ key, title: viewName }) => {
        const { viewId, hidden } = extractKey(key);

        return (
          <Space direction="horizontal" align="center">
            <Checkbox
              checked={!hidden}
              onClick={() => updateVisibility(viewId, !hidden)}
            />
            <ViewName>{viewName}</ViewName>
          </Space>
        );
      }}
      icon={<Draggable16 />}
    />
  );
};
